import * as Sentry from '@sentry/browser';
import {captureConsoleIntegration, thirdPartyErrorFilterIntegration} from '@sentry/browser';

// Keep in sync with sentryApplicationKey in vite.config.mjs, which stamps it into every bundle.
export const SENTRY_APPLICATION_KEY = 'pears-frontend';

const dsn = document.querySelector('meta[name="sentry-dsn"]')?.content,
      environment = document.querySelector('meta[name="sentry-environment"]')?.content,
      release = document.querySelector('meta[name="sentry-release"]')?.content,
      enabled = document.querySelector('meta[name="sentry-enabled"]')?.content === 'true';

/**
 * Returns the release to report, or undefined when the server doesn't know its commit.
 * Sending `pears-prod@unknown` would lump every deploy into one release.
 */
export function getRelease(value) {
  return value && !value.endsWith('@unknown') ? value : undefined;
}

/**
 * Clears the third_party_code tag when a frame came from a script on our own origin.
 *
 * Inline <script> blocks in Django templates aren't built by Vite, so they carry no application
 * key and thirdPartyErrorFilterIntegration counts them as third-party. Their frames report the
 * page URL, which a browser extension's script never does.
 */
export function keepSameOriginErrors(event, origin = window.location.origin) {
  if (event.tags?.third_party_code !== true) {
    return event;
  }
  const frames = (event.exception?.values ?? []).flatMap((value) => value.stacktrace?.frames ?? []);
  if (frames.some((frame) => frame.filename?.startsWith(`${origin}/`))) {
    delete event.tags.third_party_code;
  }
  return event;
}

/**
 * The beforeSend hook. Sentry calls it as (event, hint), so it must not hand the hint to
 * keepSameOriginErrors, whose second parameter is the origin.
 */
function beforeSend(event) {
  return keepSameOriginErrors(event);
}

if (dsn && (environment !== 'dev' || enabled)) {
  Sentry.init({
    dsn,
    environment,
    release: getRelease(release),
    integrations: [
      captureConsoleIntegration({levels: ['error']}),
      // Tag, don't drop: an alert rule can then ignore errors whose stack is entirely outside our
      // bundles while the events stay searchable.
      thirdPartyErrorFilterIntegration({
        filterKeys: [SENTRY_APPLICATION_KEY],
        behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
      }),
    ],
    beforeSend,
  });
}
